import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { PageContainer, RcsSimpleStepper, RcsSimpleStepperItem, RcsSimpleStepperItemStatus, } from '@guildeducationinc/recess';
import { Attestations } from '@src/components/Employee/EditProgram/Attestations';
import { ProgramStep, ProgramStepFields } from '@src/components/Employee/EditProgram/ProgramFields';
import { ProgramReview } from '@src/components/Employee/EditProgram/ProgramReview';
import { ProgramStepInput } from '@src/components/Employee/EditProgram/ProgramStepInput';
import { FormActions } from '@src/components/Employee/FormActions/FormActions';
import { PaymentRequestMessages } from '@src/components/Employee/PaymentRequestMessages';
import { EditExpenseModal } from '@src/components/Employee/PaymentRequests/EditPaymentRequest/EditExpenseModal';
import { Expenses } from '@src/components/Employee/PaymentRequests/EditPaymentRequest/Expenses';
import { FlowContainer } from '@src/components/FlowContainer/FlowContainer';
import { TopBar } from '@src/components/TopBar';
import { ProgramContext } from '@src/contexts/ProgramContextProvider';
import { SAVE_PROGRAM } from '@src/graphql/queries/program';
import { useAuth } from '@src/hooks/useAuth';
import { useBackTo } from '@src/hooks/useBackTo';
import { usePrograms } from '@src/hooks/usePrograms';
import { useSavePaymentRequest } from '@src/hooks/useSavePaymentRequest';
import { PaymentMethod, PaymentRequestStatus, PreimbursementStatus, ProgramStatus, RuleType, } from '@src/types';
import { getSchoolPolicy, requireFieldsOfStudy, requirePickListValue, requireProgramName, requireProvider, requireSchool, } from '@src/utilities/benefitsHelper';
import { prepareForSave } from '@src/utilities/gqlHelper';
import { formatMessageDates } from '@src/utilities/messagesHelper';
import { isUploadDocumentRequired, isWithinDeadline, isWithinDocumentDeadline, } from '@src/utilities/paymentRequestHelper';
import classNames from 'classnames';
import { isEmpty, omit, snakeCase } from 'lodash';
import { ContentType, MessageType, getQueryParam, mapKeysDeep, trackEvent, useContents, useLocale, } from 'pe-core-frontend';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './EditProgram.module.scss';
const fixPaymentRequestCasing = (paymentRequest) => {
    const obj = mapKeysDeep(paymentRequest, snakeCase);
    // in documents, convert s_3_key to s3_key
    if (obj.documents) {
        obj.documents.docs = obj.documents.docs.map((doc) => {
            return doc.s_3_key ? { ...omit(doc, 's_3_key'), s3_key: doc.s_3_key } : doc;
        });
    }
    return obj;
};
export const EditProgram = ({ backLink = '/' }) => {
    const { t } = useLocale();
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const backTo = useBackTo();
    const [saveProgram] = useMutation(SAVE_PROGRAM);
    const { program, setProgram, myBenefits, getPolicy } = useContext(ProgramContext);
    const { activePrograms } = usePrograms();
    const [submitting, setSubmitting] = useState(false);
    const [messages, setMessages] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [currentStep, setCurrentStep] = useState((getQueryParam('step') || ProgramStep.ABOUT));
    const [attestationContentIds, setAttestationContentIds] = useState([]);
    const [signature, setSignature] = useState('');
    const [validated, setValidated] = useState({});
    const requireName = useMemo(() => requireProgramName(myBenefits, program), [myBenefits, program]);
    const schoolPolicy = useMemo(() => getSchoolPolicy(myBenefits, program), [myBenefits, program]);
    const programPolicy = useMemo(() => getPolicy(RuleType.PROGRAM, program), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [program]);
    const { itemErrors, 
    // requestErrors,
    itemToEdit, 
    // errors,
    // setErrors,
    setItemToEdit, updateItem, handleValidateItem, 
    // handleValidateRequest,
    // save,
    handleAddItem, handleItemChange, handleItemRemove, paymentRequest, setPaymentRequest, resetItemToEdit, prepareRequestForSave, eligibilityLock, } = useSavePaymentRequest({
        initialPaymentRequest: {
            id: 0,
            clientId: program.clientId,
            programId: program.id,
            documents: { docs: [] },
            status: PaymentRequestStatus.PENDING,
            requestNum: '',
            capYear: 0,
            paymentRequestItems: [],
            extraData: {},
            paymentMethod: PaymentMethod.REIMBURSEMENT,
        },
    });
    const showExpenses = !eligibilityLock.locked ||
        (eligibilityLock.canSubmitExpenses && paymentRequest.requiresExpenses);
    const approvalPolicy = getPolicy(RuleType.APPROVAL_CHAIN, program);
    const showUploadProof = paymentRequest.status === PaymentRequestStatus.AWAITING_DOCUMENTS ||
        (paymentRequest.paymentMethod === PaymentMethod.PREIMBURSEMENT &&
            paymentRequest.preimbursementStatus === PreimbursementStatus.PENDING_PROOF);
    const documentRequired = useMemo(() => {
        return isUploadDocumentRequired({
            paymentMethod: paymentRequest.paymentMethod,
            termStartDate: program.startDate,
            termEndDate: program.startDate,
        }, approvalPolicy, t);
    }, [paymentRequest, approvalPolicy, t]);
    // Determine if we should show the "requires expenses" radio buttons for single expense
    const showRequiresExpenses = useMemo(() => program.isSingleExpense &&
        !eligibilityLock.preimbursement &&
        eligibilityLock.canSubmitExpenses &&
        isWithinDeadline({
            paymentMethod: paymentRequest.paymentMethod,
            termStartDate: program.startDate,
            termEndDate: program.estCompDate,
        }, approvalPolicy), [
        program.isSingleExpense,
        eligibilityLock.preimbursement,
        eligibilityLock.canSubmitExpenses,
        paymentRequest.paymentMethod,
        program.startDate,
        program.estCompDate,
        approvalPolicy,
    ]);
    const requiresPreapprovalAttestation = useMemo(() => {
        if (program.isSingleExpense === false || !paymentRequest)
            return false;
        if (paymentRequest.paymentMethod === PaymentMethod.PREIMBURSEMENT)
            return false;
        const approvalPolicyConfig = approvalPolicy?.paymentRequestApproval;
        if (!approvalPolicyConfig)
            return false;
        return (approvalPolicyConfig.enableWorkflow &&
            approvalPolicyConfig.workflowId &&
            !isWithinDocumentDeadline({
                paymentMethod: paymentRequest.paymentMethod,
                termStartDate: program.startDate,
                termEndDate: program.startDate,
            }, approvalPolicy, t).success);
    }, [getPolicy, program, paymentRequest, approvalPolicy]);
    const { contents: attestationContents } = useContents({
        clientId: program.clientId,
        contentType: ContentType.OTHER,
        extraData: {
            type: 'attestations',
            attestationType: program.isSingleExpense &&
                paymentRequest &&
                paymentRequest.paymentMethod === PaymentMethod.PREIMBURSEMENT
                ? paymentRequest.preimbursementStatus
                    ? 'payment_request_preimbursement_upload_proof'
                    : 'payment_request_preimbursement'
                : requiresPreapprovalAttestation
                    ? 'payment_request_preapproval'
                    : 'program',
        },
    });
    const { contents: paymentRequestAttestationContents } = useContents({
        skip: !program.isSingleExpense,
        clientId: program.clientId,
        contentType: ContentType.OTHER,
        extraData: {
            type: 'attestations',
            attestationType: 'payment_request',
        },
    });
    // Combine them as needed
    const combined = program.isSingleExpense
        ? [...attestationContents, ...paymentRequestAttestationContents]
        : attestationContents;
    // Deduplicate by name
    const allAttestationContents = combined.filter((item, index, self) => item.name && self.findIndex(i => i.name === item.name) === index);
    // call analytics on first render
    useEffect(() => {
        trackEvent('ProgramRequestStarted');
    }, []);
    useEffect(() => {
        if (validated[currentStep]) {
            setTimeout(() => {
                const { errors } = handleValidate(currentStep, true); // revalidate on change if already validated
                setFieldErrors(errors);
            }, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [program, currentStep, validated]);
    useEffect(() => {
        if (requireName)
            return;
        // if program name is not asked for, default to using the field of study
        setProgram(prg => ({
            ...prg,
            programName: prg.custom1 || prg.otherFieldOfStudy || prg.fieldOfStudyName || '',
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requireName, program.custom1, program.otherFieldOfStudy, program.fieldOfStudyName]);
    // Sync payment request term dates with program dates for single expense
    useEffect(() => {
        if (program.isSingleExpense && (program.startDate || program.estCompDate)) {
            setPaymentRequest(prev => ({
                ...prev,
                termName: program.programName,
                termStartDate: program.startDate || prev.termStartDate,
                termEndDate: program.estCompDate || prev.termEndDate,
            }));
        }
    }, [
        program.isSingleExpense,
        program.programName,
        program.startDate,
        program.estCompDate,
        setPaymentRequest,
    ]);
    // Set default requiresExpenses when radio buttons are hidden
    useEffect(() => {
        if (program.isSingleExpense && !showRequiresExpenses) {
            // Default requiresExpenses to True when preimbursement or status is not PENDING/IN_REVIEW
            const defaultRequiresExpenses = eligibilityLock.preimbursement ||
                ![PaymentRequestStatus.PENDING, PaymentRequestStatus.IN_REVIEW].includes(paymentRequest.status)
                ? true
                : eligibilityLock.expensesRequired;
            if (paymentRequest.requiresExpenses !== defaultRequiresExpenses) {
                setPaymentRequest(prev => ({
                    ...prev,
                    requiresExpenses: defaultRequiresExpenses,
                }));
            }
        }
    }, [
        program.isSingleExpense,
        showRequiresExpenses,
        eligibilityLock.preimbursement,
        eligibilityLock.expensesRequired,
        paymentRequest.status,
        paymentRequest.requiresExpenses,
        setPaymentRequest,
    ]);
    const handleCancel = () => {
        setMessages([]);
        if (currentStep === ProgramStep.ABOUT) {
            if (activePrograms.length === 0) {
                backTo('/');
            }
            else {
                navigate('/requests');
            }
        }
        else if (currentStep === ProgramStep.REVIEW) {
            if (!program.isSingleExpense || showExpenses) {
                setCurrentStep(ProgramStep.DETAILS);
            }
            else {
                setCurrentStep(ProgramStep.ABOUT);
            }
        }
        else {
            setCurrentStep(ProgramStep.ABOUT);
        }
    };
    const handleValidate = (step = currentStep, skipValidateUpdate = false) => {
        const errors = {};
        const messages = [];
        if (step === ProgramStep.ABOUT) {
            if (!program.programName) {
                errors.programName = t('{field} is required', {
                    values: { field: t('Program/Expense Name') },
                });
            }
            if (!program.programLevelId) {
                errors.programLevelId = t('{field} is required', { values: { field: t('Expense Type') } });
            }
            if (requireProvider(myBenefits, program) && !program.schoolName) {
                errors.schoolName = t('{field} is required', { values: { field: t('Provider') } });
            }
            if (requireSchool(myBenefits, program)) {
                if (program.schoolId === -1 && !program.otherSchool) {
                    errors.otherSchool = t('{field} is required', { values: { field: t('Other school') } });
                }
                else if (!program.schoolId && !program.otherSchool) {
                    errors.schoolId = t('{field} is required', { values: { field: t('School Name') } });
                }
                if (program.schoolId === -1 && !program.otherSchoolCountryCode) {
                    errors.otherSchoolCountryCode = t('{field} is required', {
                        values: { field: t('Other School Country') },
                    });
                }
            }
            if (requireFieldsOfStudy(myBenefits, program)) {
                if (program.fieldOfStudyId === -1 && !program.otherFieldOfStudy) {
                    errors.otherFieldOfStudy = t('{field} is required', {
                        values: { field: t('Other field of study') },
                    });
                }
                else if (!program.fieldOfStudyId && !program.otherFieldOfStudy) {
                    errors.fieldOfStudyId = t('{field} is required', {
                        values: { field: t('Field of Study') },
                    });
                }
            }
            if (requirePickListValue(myBenefits, program) && !program.custom1) {
                errors.custom1 = t('{field} is required', {
                    values: { field: schoolPolicy?.pickList?.label || t('Pick list') },
                });
            }
            if (program.isSingleExpense === undefined) {
                errors.isSingleExpense = t('required');
            }
            if (eligibilityLock.preimbursement && program.isSingleExpense) {
                if (!paymentRequest.paymentMethod) {
                    errors.paymentMethod = t('required');
                }
            }
            // Validate amountRequested when RequestFunding is shown
            if (program.isSingleExpense && paymentRequest.requiresExpenses === false) {
                const budgetPolicy = getPolicy(RuleType.BUDGET, program);
                if (budgetPolicy && budgetPolicy.approvalMode) {
                    if (!paymentRequest.amountRequested || paymentRequest.amountRequested === 0) {
                        errors.amountRequested = t('required');
                    }
                    else if (paymentRequest.amountRequested < 0) {
                        errors.amountRequested = t('must be greater than 0');
                    }
                    else if (isNaN(paymentRequest.amountRequested)) {
                        errors.amountRequested = t('must be a decimal number');
                    }
                }
            }
        }
        if (step === ProgramStep.DETAILS || program.isSingleExpense) {
            if (!program.startDate) {
                errors.startDate = t('{field} is required', {
                    values: {
                        field: program.isSingleExpense
                            ? programPolicy?.singleExpenseDate?.label ?? t('Purchase date')
                            : t('Initial start date'),
                    },
                });
            }
            if (!program.estCompDate) {
                errors.estCompDate = t('{field} is required', {
                    values: { field: 'Completion date' },
                });
            }
            if (program.startDate && program.estCompDate) {
                if (new Date(program.startDate) > new Date(program.estCompDate)) {
                    errors.estCompDate = t('End date must be after start date');
                }
            }
            const purpose = program.purpose;
            if (purpose.isActive &&
                purpose.isRequired &&
                (!purpose.selections ||
                    purpose.selections.length === 0 ||
                    (purpose.selections.includes('Other') && !purpose.other))) {
                errors.purpose = t('{field} is required', { values: { field: t('Purpose') } });
            }
        }
        if (step === ProgramStep.DETAILS && program.isSingleExpense) {
            if (showExpenses && paymentRequest.paymentRequestItems.length === 0) {
                messages.push({ type: MessageType.ERROR, value: t('Expenses are required') });
            }
            if (documentRequired && paymentRequest.documents.docs.length === 0) {
                errors.documents = t('{field} is required', { values: { field: 'Document' } });
            }
            if (messages.length > 0) {
                return { errors, messages };
            }
        }
        if (step === ProgramStep.REVIEW) {
            if (allAttestationContents && allAttestationContents.length > 0) {
                if (!signature) {
                    errors.signature = t('{field} is required', { values: { field: t('Signature') } });
                }
                else if (signature.toLocaleLowerCase() !==
                    `${currentUser?.firstName} ${currentUser?.lastName}`.toLocaleLowerCase()) {
                    errors.signature = t('Signature does not match your full name');
                }
                if (attestationContentIds.length !== allAttestationContents.length) {
                    errors.attestations = t('Please accept all agreements');
                }
            }
        }
        if (!skipValidateUpdate) {
            setValidated(prev => ({ ...prev, [step]: true }));
        }
        return { errors, messages };
    };
    const isStepComplete = (step) => {
        if (step === ProgramStep.REVIEW) {
            return false;
        }
        const { errors, messages } = handleValidate(step, true);
        return isEmpty(errors) && isEmpty(messages);
    };
    const handleOk = async () => {
        const { errors, messages } = handleValidate();
        setFieldErrors(errors);
        setMessages(messages);
        if (!isEmpty(errors) || !isEmpty(messages)) {
            return;
        }
        if (currentStep === ProgramStep.REVIEW) {
            await handleSubmit();
        }
        else {
            if (program.isSingleExpense) {
                // perform validation on the payment request only for single expense programs
                const result = await handleSubmit(true);
                if (!result?.data?.saveProgram?.success) {
                    const messages = formatMessageDates(result?.data?.saveProgram?.messages || []).filter(m => m.value !== 'Expenses are required');
                    if (messages.length > 0) {
                        setMessages(messages);
                        return;
                    }
                }
            }
            if (currentStep === ProgramStep.ABOUT) {
                if (!program.isSingleExpense || showExpenses) {
                    setCurrentStep(ProgramStep.DETAILS);
                }
                else {
                    setCurrentStep(ProgramStep.REVIEW);
                }
            }
            else if (currentStep === ProgramStep.DETAILS) {
                setCurrentStep(ProgramStep.REVIEW);
            }
        }
    };
    const handleSubmit = async (validateOnly = false) => {
        setMessages([]);
        setSubmitting(true);
        try {
            const isNew = !program.id;
            const result = await saveProgram({
                variables: {
                    input: {
                        ...prepareForSave(program),
                        status: ProgramStatus.IN_REVIEW,
                        attestationContentIds,
                        description: program.description || '',
                        fieldOfStudyId: program.fieldOfStudyId === -1 ? null : program.fieldOfStudyId,
                        schoolId: program.schoolId === -1 ? null : program.schoolId,
                        paymentRequest: program.isSingleExpense
                            ? fixPaymentRequestCasing(prepareRequestForSave(paymentRequest))
                            : undefined,
                        validateOnly,
                    },
                },
            });
            if (validateOnly) {
                return result;
            }
            if (isNew && result.data?.saveProgram?.success) {
                const { paymentRequest, program } = result.data.saveProgram;
                if (paymentRequest) {
                    // close program started tracking
                    trackEvent('ProgramRequestSubmitted', {
                        program_id: program?.id,
                    });
                    // tracking for ReimbursementRequest
                    trackEvent('ReimbursementRequestStarted', {
                        program_id: program?.id,
                    });
                    trackEvent('ReimbursementRequestSubmitted', {
                        program_id: program?.id,
                        reimbursement_id: paymentRequest.id,
                    });
                    navigate(`/payments/${paymentRequest.id}/submitted?returnTo=${encodeURIComponent(backLink)}`);
                }
                else {
                    trackEvent('ProgramRequestSubmitted', {
                        program_id: program.id,
                    });
                    navigate(`/programs/${program.id}/submitted?returnTo=${encodeURIComponent(backLink)}`);
                }
            }
            else {
                setMessages(formatMessageDates(result.data?.saveProgram?.messages));
            }
        }
        catch (e) {
            setMessages([{ type: MessageType.ERROR, value: t('Error processing request') }]);
        }
        finally {
            setSubmitting(false);
        }
    };
    const handleStepClick = (step) => (e) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        e.preventDefault();
        // can go back to any step from review
        if (currentStep === ProgramStep.REVIEW) {
            setCurrentStep(step);
            return false;
        }
        if (currentStep === ProgramStep.DETAILS && step === ProgramStep.ABOUT) {
            setCurrentStep(step);
        }
        else {
            if (isStepComplete(currentStep)) {
                setCurrentStep(step);
            }
        }
        return false;
    };
    return (_jsxs("div", { children: [_jsx(TopBar, { backLink: backLink }), _jsx(PageContainer, { children: _jsxs(FlowContainer, { children: [_jsxs("div", { className: classNames(styles.form), children: [_jsx("div", { className: 'mb-4', children: _jsx(RcsSimpleStepper, { className: styles.stepper, children: [
                                            'about',
                                            ...(!program.isSingleExpense || showExpenses ? ['details'] : []),
                                            'review',
                                        ].map(stepName => (_jsx(RcsSimpleStepperItem, { LinkComponent: Link, href: '?type=program#', "aria-current": stepName === currentStep ? 'step' : false, status: isStepComplete(stepName)
                                                ? RcsSimpleStepperItemStatus.Completed
                                                : RcsSimpleStepperItemStatus.Incomplete, label: t(`ProgramStep.${stepName}`), onClick: handleStepClick(stepName) }, stepName))) }) }), _jsxs("div", { className: styles.step, children: [_jsx(PaymentRequestMessages, { paymentRequest: paymentRequest, messages: messages, onClose: () => setMessages([]) }), currentStep === ProgramStep.ABOUT && (_jsx(ProgramStepInput, { program: program, paymentRequest: paymentRequest, title: t('About your expense'), description: t('Tell us about your program or expense to check if it is covered and request reimbursement.'), fields: ProgramStepFields[currentStep].concat(program.isSingleExpense ? ProgramStepFields.details : []), onProgramChange: setProgram, onPaymentRequestChange: setPaymentRequest, eligibilityLock: eligibilityLock, errors: fieldErrors, messages: messages, helpLink: '/reimbursement-policy', showRequiresExpenses: showRequiresExpenses })), currentStep === ProgramStep.DETAILS &&
                                            (program.isSingleExpense ? (_jsx(Expenses, { program: program, paymentRequest: paymentRequest, onChange: setPaymentRequest, onAdd: handleAddItem, onRemove: handleItemRemove, onEdit: setItemToEdit, requestErrors: fieldErrors, title: t('Add your {progLevel} expenses', {
                                                    values: { progLevel: program.programLevelName },
                                                }), documentRequired: documentRequired })) : (_jsx(ProgramStepInput, { program: program, paymentRequest: paymentRequest, title: t('A few more details about your {progLevel}', {
                                                    values: { progLevel: program.programLevelName },
                                                }), fields: ProgramStepFields[currentStep], onProgramChange: setProgram, onPaymentRequestChange: setPaymentRequest, eligibilityLock: eligibilityLock, errors: fieldErrors, messages: messages, showRequiresExpenses: showRequiresExpenses }))), currentStep === ProgramStep.REVIEW && (_jsxs(_Fragment, { children: [_jsx(ProgramReview, { program: program, onEdit: setCurrentStep, onChange: setProgram, paymentRequest: paymentRequest, readonly: showUploadProof, eligibilityLock: eligibilityLock }), allAttestationContents && allAttestationContents.length > 0 && (_jsx(Attestations, { contents: allAttestationContents, attestationContentIds: attestationContentIds || [], setAttestationContentIds: setAttestationContentIds, signature: signature, setSignature: setSignature, errors: fieldErrors }))] }))] })] }), _jsx(FormActions, { showBackArrow: true, cancelText: t('Previous'), okText: currentStep !== ProgramStep.REVIEW ? t('Continue') : t('Submit'), onCancel: handleCancel, onOk: handleOk, disabled: submitting }), itemToEdit && (_jsx(EditExpenseModal, { item: itemToEdit, onClose: resetItemToEdit, onChange: handleItemChange, errors: itemErrors, onValidate: handleValidateItem, onSubmit: updateItem, paymentRequest: paymentRequest }))] }) })] }));
};
